// Guards against open-redirect / phishing when navigating to a URL that
// originated from a backend response or a user-controlled param.
//
// Even though our own endpoints don't currently echo attacker-controlled
// URLs, `window.location.href = data.url` is a foot-gun: one future
// endpoint that reflects user input would turn into a phishing pivot.
// These helpers fail closed.

// Hosts we are willing to hard-redirect the top window to.
const ALLOWED_REDIRECT_HOSTS = [
  'billing.stripe.com',
  'checkout.stripe.com',
  'connect.stripe.com',
  'accounts.google.com',
];

function hostOf(url) {
  try {
    return new URL(url, window.location.origin).host.toLowerCase();
  } catch {
    return null;
  }
}

// True if `url` is https and its host is the exact allowed host or a
// subdomain of it.
export function isAllowedRedirectUrl(url, allowed = ALLOWED_REDIRECT_HOSTS) {
  if (typeof url !== 'string' || !url) return false;
  let parsed;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.host.toLowerCase();
  return allowed.some((h) => host === h || host.endsWith(`.${h}`));
}

// Navigate the top window to an external URL ONLY if it passes the
// allowlist. Returns true if it navigated, false (and does nothing) if
// the URL was rejected — callers should surface an error in that case.
export function safeExternalRedirect(url, allowed = ALLOWED_REDIRECT_HOSTS) {
  if (!isAllowedRedirectUrl(url, allowed)) return false;
  window.location.href = url;
  return true;
}

// Open an external URL in a new tab (with a top-window fallback) ONLY if
// it passes the allowlist. Returns true if it opened/navigated.
export function safeExternalOpen(url, allowed = ALLOWED_REDIRECT_HOSTS) {
  if (!isAllowedRedirectUrl(url, allowed)) return false;
  // No 'noopener' feature string: with it window.open returns null even when the tab opened, so the fallback would
  // navigate Strata too. Cut the opener by hand instead.
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
  } else {
    try { window.top.location.href = url; } catch { window.location.href = url; }
  }
  return true;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Validate an id that gets interpolated into a same-origin URL path, so a
// malformed/injected value can't break the URL or smuggle extra path/query.
export function isUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

export { ALLOWED_REDIRECT_HOSTS };
